import React, { useState, useEffect } from 'react';

// Utils
import { formatCurrency, getAlert, useEscapeClose } from '../../../utils/SamuiUtils';
import { useModalKeyboardNavigation, scrollToRow, getRowStyle, getKeyboardInstructions } from '../../../utils/ModalKeyboardNavigation';
import { formatDate } from '../../../utils/DateUtils';

const ItemOrderModal = ({
    mode,
    showItemModal,
    handleItemClose,
    itemDataList,
    onRowSelectItem,
    formDetailList,
    setFormDetailList,
    selectedItems,
    setSelectedItems,
    selectedStockItems,
    setSelectedStockItems,
    odDataList,
    onRowSelectOd
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredItemDataList, setFilteredItemDataList] = useState([]);
    const [filteredOdDataList, setFilteredOdDataList] = useState([]);
    const [selectedType, setSelectedType] = useState('OD');
    const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับ keyboard navigation

    useEscapeClose(showItemModal, handleItemClose);

    // Custom handler สำหรับ ItemOrderModal
    const customSpaceHandler = () => {
        if (selectedType === 'OD' && filteredOdDataList[selectedRowIndex]) {
            handleCheckboxChange(filteredOdDataList[selectedRowIndex]);
        } else if (selectedType === 'ST' && filteredItemDataList[selectedRowIndex]) {
            const filteredStockItems = filteredItemDataList.filter(item => item.Item_Onhand > 0);
            if (filteredStockItems[selectedRowIndex]) {
                handleStockItemCheckboxChange(filteredStockItems[selectedRowIndex]);
            }
        }
    };

    // เพิ่ม keyboard navigation
    const currentList = selectedType === 'OD' ? filteredOdDataList : filteredItemDataList.filter(item => item.Item_Onhand > 0);
    const currentSelectedItems = selectedType === 'OD' ? selectedItems : selectedStockItems;
    
    useModalKeyboardNavigation(
        showItemModal,
        currentList,
        selectedRowIndex,
        setSelectedRowIndex,
        null, // ไม่ใช้ default onRowSelect
        handleItemClose,
        {
            customEnterHandler: () => handleConfirmSelection(),
            customSpaceHandler,
            enableSpace: true,
            includeHighlightedOnEnter: true,
            selectedItems: currentSelectedItems
        }
    );
    
    useEffect(() => {
        setFilteredItemDataList(
            itemDataList.filter(item =>
                item.Item_Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.Item_Name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
    }, [searchTerm, itemDataList]);

    useEffect(() => {
        const sortedOdDataList = [...odDataList].sort((a, b) => {
            if (a.WHDoc_No < b.WHDoc_No) return 1;
            if (a.WHDoc_No > b.WHDoc_No) return -1;
            return 0;
        });

        setFilteredOdDataList(sortedOdDataList);
        setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
    }, [odDataList]);

    // รีเซ็ต selectedRowIndex เมื่อเปลี่ยนประเภท
    useEffect(() => {
        setSelectedRowIndex(0);
    }, [selectedType]);

    useEffect(() => {
        if (showItemModal) {
            setSearchTerm('');
            setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
            autoSelectOd();
        }
    }, [showItemModal, mode]);

    const autoSelectOd = () => {
        const refDocNoMap = formDetailList.reduce((acc, detail) => {
            const refDocNo = detail.refDocNo;
            if (!acc[refDocNo]) {
                acc[refDocNo] = [];
            }
            acc[refDocNo].push(detail);
            return acc;
        }, {});

        const selectedOdNos = new Set();
        Object.keys(refDocNoMap).forEach(refDocNo => {
            filteredOdDataList.forEach(od => {
                if (od.WHDoc_No.trim() === refDocNo) {
                    selectedOdNos.add(od);
                }
            });
        });

        setSelectedItems(Array.from(selectedOdNos));
    };

    const autoSelectItem = () => {
        const selectedItemIds = new Set(
            formDetailList
                .filter(detail => detail.refDocId === null)
                .map(detail => detail.itemId)
        );

        const selectedItemsFromStock = itemDataList.filter(item => selectedItemIds.has(item.Item_Id));

        setSelectedStockItems(selectedItemsFromStock);
    };


    useEffect(() => {
        if (showItemModal) {
            setSearchTerm('');
            autoSelectItem(); // เรียก autoSelectItem เมื่อ modal เปิดใหม่
        }
    }, [showItemModal]); // ใช้แค่ showItemModal เป็น dependency

    const handleCheckboxChange = (od) => {
        if (selectedItems.some(item => item.WHDoc_No === od.WHDoc_No)) {
            // กรณียกเลิกการเลือก (uncheck)
            setSelectedItems(selectedItems.filter(item => item.WHDoc_No !== od.WHDoc_No));
            // setFormDetailList(prevFormDetailList =>
            //     prevFormDetailList.filter(item => item.refDocNo !== od.WHDoc_No)
            // );
        } else {
            // กรณีเลือก (check)
            setSelectedItems([...selectedItems, od]);
        }
    };

    const handleStockItemCheckboxChange = (item) => {
        if (selectedStockItems.some(selectedItem => selectedItem.Item_Id === item.Item_Id)) {
            // กรณียกเลิกการเลือก (uncheck)
            setSelectedStockItems(selectedStockItems.filter(selectedItem => selectedItem.Item_Id !== item.Item_Id));
            // setFormDetailList(prevFormDetailList =>
            //     prevFormDetailList.filter(detail => detail.itemId !== item.Item_Id)
            // );
        } else {
            // กรณีเลือก (check)
            setSelectedStockItems([...selectedStockItems, item]);
        }
    };

    const handleConfirmSelection = async () => {
        if (selectedType === 'OD' && selectedItems.length === 0) {
            getAlert('FAILED', 'กรุณาเลือกใบออเดอร์สินค้า');
            return; // หยุดการทำงานของฟังก์ชันถ้าไม่มีการเลือก
        }

        if (selectedType === 'ST' && selectedStockItems.length === 0) {
            getAlert('FAILED', 'กรุณาเลือกสินค้าจากคลัง');
            return; // หยุดการทำงานของฟังก์ชันถ้าไม่มีการเลือกสินค้า
        }

        if (selectedType === 'OD') {
            selectedItems.forEach(od => {
                onRowSelectOd(od);
            });
        } else if (selectedType === 'ST') {
            selectedStockItems.forEach(item => {
                onRowSelectItem(item);
            });
        }

        // อัปเดต FormDetailList เมื่อมีการยกเลิกการเลือกใน Modal
        const remainingRefDocNos = new Set(selectedItems.map(item => item.WHDoc_No));
        const remainingItemIds = new Set(selectedStockItems.map(item => item.Item_Id));

        setFormDetailList(prevFormDetailList =>
            prevFormDetailList.filter(item =>
                (item.refDocNo && remainingRefDocNos.has(item.refDocNo)) ||
                (item.itemId && remainingItemIds.has(item.itemId))
            )
        );

        handleItemClose();
    };

    return (
        <>
            <div
                className={`modal ${showItemModal ? 'show' : ''}`}
                style={{ display: showItemModal ? 'block' : 'none' }}
                tabIndex="-1"
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายชื่อใบออเดอร์สินค้า & สินค้าในสต็อกคลัง</h5>
                            <button type="button" className="close" onClick={handleItemClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex align-items-center">
                                <div className="col-4">
                                    <div className="form-group mb-0 ms-3">
                                        <span className="fw-bold">ประเภทการเลือกเบิก</span>
                                        <div className="radio-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="selectedType"
                                                value="OD"
                                                id="typeOD"
                                                checked={selectedType === 'OD'}
                                                onChange={() => setSelectedType('OD')}
                                            />
                                            <label className="form-check-label" htmlFor="typeOD">ใบออเดอร์สินค้า</label>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="selectedType"
                                                value="ST"
                                                id="typeST"
                                                checked={selectedType === 'ST'}
                                                onChange={() => setSelectedType('ST')}
                                            />
                                            <label className="form-check-label me-2" htmlFor="typeST">สินค้าในคลัง</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-8">
                                    <div className="form-group mb-0">
                                        <span className="fw-bold">ค้นหา</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ค้นหา ITEM_CODE หรือ ITEM_NAME"
                                            value={searchTerm || ''}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ใบออเดอร์สินค้า */}
                            {selectedType === 'OD' && (
                                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <table className="table table-striped table-hover">
                                        <thead className="thead-dark">
                                            <tr>
                                                <th className="text-center" style={{ width: '1%' }}>
                                                    <input type="checkbox" onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setSelectedItems(checked ? filteredOdDataList : []);
                                                    }} checked={filteredOdDataList.length > 0 && selectedItems.length === filteredOdDataList.length} />
                                                </th>
                                                <th className="text-center" style={{ width: '18%' }}>เลขที่เอกสาร (OD)</th>
                                                <th className="text-center" style={{ width: '30%' }}>ชื่อลูกค้า</th>
                                                <th className="text-center" style={{ width: '30%' }}>รายละเอียดเอกสาร</th>
                                                <th className="text-center" style={{ width: '12%' }}>วันที่ใบออเดอร์</th>
                                                <th className="text-center" style={{ width: '12%' }}>วันที่จัดส่ง</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredOdDataList.length > 0 ? (
                                                filteredOdDataList.map((od, index) => (
                                                    <tr
                                                        key={od.Doc_ID || index + 1}
                                                        onClick={() => { 
                                                            setSelectedRowIndex(index);
                                                            handleCheckboxChange(od); 
                                                        }}
                                                        style={getRowStyle(index, selectedRowIndex)}
                                                    >
                                                        <td className="text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedItems.some(item => item.WHDoc_No === od.WHDoc_No)}
                                                                onChange={() => handleCheckboxChange(od)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>
                                                        <td className="text-center">{od.WHDoc_No}</td>
                                                        <td className="text-left">{od.WHDoc_Cust_Name}</td>
                                                        <td className="text-left">{od.WHDoc_Remark}</td>
                                                        <td className="text-center">{formatDate(od.WHDoc_Date)}</td>
                                                        <td className="text-center">{formatDate(od.WHDoc_TransDate)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="text-center">ไม่พบข้อมูล</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* สินค้าในคลัง */}
                            {selectedType === 'ST' && (
                                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <table className="table table-striped table-hover">
                                        <thead className="thead-dark">
                                            <tr>
                                                <th className="text-center" style={{ width: '1%' }}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setSelectedStockItems(checked ? filteredItemDataList : []);
                                                        }}
                                                        checked={filteredItemDataList.length > 0 && selectedStockItems.length === filteredItemDataList.length}
                                                    />
                                                </th>
                                                <th className="text-center" style={{ width: '18%' }}>รหัสสินค้า</th>
                                                <th className="text-center" style={{ width: '60%' }}>ชื่อสินค้า</th>
                                                <th className="text-center" style={{ width: '12%' }}>จำนวน</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredItemDataList.length > 0 ? (
                                                filteredItemDataList
                                                    .filter(item => item.Item_Onhand > 0)
                                                    .map((item, index) => (
                                                        <tr
                                                            key={item.Item_Id || index + 1}
                                                            onClick={() => { 
                                                                setSelectedRowIndex(index);
                                                                handleStockItemCheckboxChange(item); 
                                                            }}
                                                            style={getRowStyle(index, selectedRowIndex)}
                                                        >
                                                            <td className="text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedStockItems.some(selectedItem => selectedItem.Item_Id === item.Item_Id)}
                                                                    onChange={() => handleStockItemCheckboxChange(item)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            </td>
                                                            <td className="text-center">{item.Item_Code}</td>
                                                            <td className="text-left">{item.Item_Name}</td>
                                                            <td className="text-center">{formatCurrency(item.Item_Onhand)}</td>
                                                        </tr>
                                                    ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center">ไม่พบข้อมูล</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </div>
                        <div className="modal-footer">
                            <div className="text-left" style={{ fontSize: "12px", color: "#666", marginRight: "auto" }}>
                                {getKeyboardInstructions("รายการ", true)}
                            </div>
                            <button type="button" className="btn btn-primary" onClick={handleConfirmSelection}>ยืนยัน</button>
                            <button type="button" className="btn btn-danger" onClick={handleItemClose}>ปิด (ESC)</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ItemOrderModal;